import SlideOutMenuV2 from "@components/SlideOutMenuV2";
import { ProjectService, Note } from "../../types";
import { truncateString } from "@utils/helperFunctions";
import EditableDataTable from "@components/EditableDataTable";
import "./style.css";
import { Form } from "react-bootstrap";
import {
  faCheck,
  faX,
  faPencil,
  faTrashCan,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import {
  useV2UpdateProjectServiceMutation,
  useV2DeleteNoteMutation,
} from "@generated";
import { formatDateAndTime } from "@utils/helpers";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import ConfirmationModal from "@components/Modals/ConfirmationModal";
import UnsavedChangesModal from "@components/UnsavedChangesModal";

interface NoteRow extends Note {
  _rowKey: string;
}

interface Props {
  resource: ProjectService | null;
  projectId: string | null;
  onClose: () => void;
}

const ManageNotesSlideout = ({ resource, projectId, onClose }: Props) => {
  const [newNote, setNewNote] = useState<{ content: string } | null>(null);
  const [notes, setNotes] = useState<NoteRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [editingRowKey, setEditingRowKey] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState<string>("");
  const [noteToDelete, setNoteToDelete] = useState<NoteRow | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] =
    useState<boolean>(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  const [updateService] = useV2UpdateProjectServiceMutation();
  const [deleteNote] = useV2DeleteNoteMutation();
  const { hasPrivilege } = usePrivilegeCheck();
  const canCreate = hasPrivilege("projects.notes", ["create", "manage"]);
  const canManage = hasPrivilege("projects.notes", ["manage"]);

  const hasUnsavedChanges = newNote !== null || editingRowKey !== null;

  const enrichNotes = (rawNotes: Note[]): NoteRow[] =>
    rawNotes.map((note, index) => ({
      ...note,
      _rowKey: `note-${note.id ?? index}-${index}`,
    }));

  useEffect(() => {
    if (resource?.notes) setNotes(enrichNotes(resource.notes));
  }, [resource]);

  const callUpdate = async (updatedNotes: Note[]) => {
    if (!resource?.id) return;
    setLoading(true);
    const data = {
      id: resource.id,
      type: "project-services",
      attributes: { notes: updatedNotes },
    };
    const response = await updateService({
      id: String(resource.id),
      //@ts-ignore
      body: { data },
    });
    //@ts-ignore
    if (!response.error) {
      //@ts-ignore
      setNotes(enrichNotes(response?.data?.data?.attributes?.notes || []));
    }
    setLoading(false);
  };

  const stripRowKey = (note: NoteRow): Note => {
    const { _rowKey, ...rest } = note;
    return rest;
  };

  const saveNewNote = async () => {
    if (!newNote?.content) return;
    await callUpdate([...notes.map(stripRowKey), { content: newNote.content }]);
    setNewNote(null);
  };

  const saveEditedNote = async () => {
    if (editingRowKey === null) return;
    const updatedNotes = notes.map((note) =>
      note._rowKey === editingRowKey
        ? { ...stripRowKey(note), content: editingContent }
        : stripRowKey(note)
    );
    await callUpdate(updatedNotes);
    setEditingRowKey(null);
    setEditingContent("");
  };

  const confirmDeleteNote = async () => {
    if (!noteToDelete?.id || !projectId) return;
    setLoading(true);
    const response = await deleteNote({
      projectId: String(projectId),
      id: String(noteToDelete.id),
    });
    //@ts-ignore
    if (!response.error) {
      setNotes((prev) =>
        prev.filter((n) => n._rowKey !== noteToDelete._rowKey)
      );
    }
    setLoading(false);
    setNoteToDelete(null);
    setShowConfirmDelete(false);
  };

  const guardUnsaved = (action: () => void) => {
    if (hasUnsavedChanges) {
      pendingActionRef.current = action;
      setShowUnsavedChangesModal(true);
    } else {
      action();
    }
  };

  const handleUnsavedCancel = () => {
    pendingActionRef.current = null;
    setShowUnsavedChangesModal(false);
  };

  const handleUnsavedDontSave = () => {
    setNewNote(null);
    setEditingRowKey(null);
    setEditingContent("");
    setShowUnsavedChangesModal(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  };

  const handleUnsavedSave = async () => {
    if (newNote !== null) {
      await saveNewNote();
    } else if (editingRowKey !== null) {
      await saveEditedNote();
    }
    setShowUnsavedChangesModal(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  };

  const columns = [
    {
      id: "note",
      header: () => "Note",
      cell: (item: NoteRow) => {
        if (item._rowKey === editingRowKey) {
          return (
            <div className="noteEditCell">
              <Form.Control
                as="textarea"
                rows={3}
                value={editingContent}
                onChange={(e) => setEditingContent(e.target.value)}
                autoFocus
                placeholder="Enter note"
              />
              <FontAwesomeIcon
                icon={faCheck}
                onClick={() => saveEditedNote()}
              />
              <FontAwesomeIcon
                icon={faX}
                onClick={() => {
                  setEditingRowKey(null);
                  setEditingContent("");
                }}
              />
            </div>
          );
        }
        const wasEdited =
          item.updated_at &&
          item.created_at &&
          item.updated_at !== item.created_at;
        return (
          <div className="noteViewCell">
            {canManage && (
              <FontAwesomeIcon
                icon={faPencil}
                className="noteEditIcon"
                onClick={() =>
                  guardUnsaved(() => {
                    setEditingRowKey(item._rowKey);
                    setEditingContent(item.content);
                  })
                }
              />
            )}
            <div className="noteContentWrapper">
              <span className="noteContent">{item.content}</span>
              {wasEdited && (
                <span className="noteUpdatedAt">
                  (Updated: {formatDateAndTime(new Date(item.updated_at!))})
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: "username",
      header: () => "By",
      cell: (item: NoteRow) => item.username ?? "",
    },
    {
      id: "created_at",
      header: () => "Created At",
      cell: (item: NoteRow) => (
        <div className="createdAtCell">
          <span>
            {item.created_at
              ? formatDateAndTime(new Date(item.created_at))
              : ""}
          </span>
          {canManage && (
            <FontAwesomeIcon
              icon={faTrashCan}
              className="noteDeleteIcon"
              onClick={() =>
                guardUnsaved(() => {
                  setNoteToDelete(item);
                  setShowConfirmDelete(true);
                })
              }
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="notesSlideout">
      <SlideOutMenuV2
        title={`Notes ${
          resource?.name ? "- " + truncateString(resource.name, 30) : ""
        }`}
        isOpen={resource ? true : false}
        onClose={() => {
          guardUnsaved(() => {
            setNewNote(null);
            setEditingRowKey(null);
            setEditingContent("");
            onClose();
          });
        }}
      >
        {loading ? (
          <SmallSpinner />
        ) : (
          <div className="manageNotesSlideout">
            {notes.length ? (
              <>
                <div className="notesSubheader">Existing Notes</div>
                <EditableDataTable
                  records={notes}
                  columns={columns}
                  config={{
                    striped: true,
                    bordered: true,
                    classForTable: newNote ? "addingNote" : "",
                  }}
                />
              </>
            ) : null}
            {newNote && (
              <div
                className={`newNote ${notes?.length % 2 ? "striped" : ""} ${
                  notes?.length ? "" : "borderTop"
                }`}
              >
                <Form.Control
                  as="textarea"
                  rows={3}
                  defaultValue={newNote?.content}
                  onChange={(e) => setNewNote({ content: e.target.value })}
                  autoFocus={true}
                  placeholder="Enter note"
                />
                <FontAwesomeIcon icon={faCheck} onClick={() => saveNewNote()} />
                <FontAwesomeIcon icon={faX} onClick={() => setNewNote(null)} />
              </div>
            )}
            {!newNote && canCreate && (
              <div
                className="addServiceBtn addNoteBtn"
                onClick={() => guardUnsaved(() => setNewNote({ content: "" }))}
              >
                + Add Note
              </div>
            )}
          </div>
        )}
      </SlideOutMenuV2>
      <ConfirmationModal
        show={showConfirmDelete}
        title="Delete Note"
        message="Are you sure you want to delete this note?"
        onConfirm={confirmDeleteNote}
        onCancel={() => {
          setNoteToDelete(null);
          setShowConfirmDelete(false);
        }}
      />
      <UnsavedChangesModal
        show={showUnsavedChangesModal}
        handleCancel={handleUnsavedCancel}
        handleDontSave={handleUnsavedDontSave}
        handleSave={handleUnsavedSave}
      />
    </div>
  );
};

export default ManageNotesSlideout;
